import React, { useState, useEffect, useContext } from "react";
import AddSale from "../components/AddSale";
import AuthContext from "../AuthContext";

function Sales() {
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [sales, setAllSalesData] = useState([]);
  const [products, setAllProducts] = useState([]);
  const [stores, setAllStores] = useState([]);
  const [updatePage, setUpdatePage] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // Search by product name
  const [filterDate, setFilterDate] = useState(""); // Filter by sale date
  const [error, setError] = useState(null); // Error messages

  const authContext = useContext(AuthContext);

  useEffect(() => {
    fetchSalesData();
    fetchProductsData();
    fetchStoresData();
  }, [updatePage]);

  // Fetching Data of All Sales
  const fetchSalesData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_sales.php`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        console.log("Raw Sales Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setAllSalesData(Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch sales error:", err);
        setAllSalesData([]);
        setError("Failed to load sales data: " + err.message);
      });
  };

  // Fetching Data of All Products
  const fetchProductsData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_products.php`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        console.log("Raw Products Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setAllProducts(Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch products error:", err);
        setAllProducts([]);
        setError("Failed to load products data: " + err.message);
      });
  };

  // Fetching Data of All Stores
  const fetchStoresData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_stores.php`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        console.log("Raw Stores Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setAllStores(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch stores error:", err);
        setAllStores([]);
        setError("Failed to load stores data: " + err.message);
      });
  };

  // Modal for Sale Add
  const addSaleModalSetting = () => {
    setShowSaleModal(!showSaleModal);
  };

  // Handle Page Update
  const handlePageUpdate = () => {
    setUpdatePage(!updatePage);
  };

  // Format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Extract YYYY-MM-DD from DATETIME
  const getDateOnly = (dateTimeString) => {
    if (!dateTimeString) return "";
    return dateTimeString.split(" ")[0]; // e.g., "2025-04-10 00:00:00" -> "2025-04-10"
  };

  // Filter sales by search query and date
  const filteredSales = sales.filter((item) => {
    const matchesSearch = searchQuery
      ? item.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesDate = filterDate
      ? getDateOnly(item.sale_date) === filterDate
      : true;
    return matchesSearch && matchesDate;
  });

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6 bg-gray-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">Sales</h1>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Modal */}
        {showSaleModal && (
          <AddSale
            addSaleModalSetting={addSaleModalSetting}
            products={products}
            stores={stores}
            handlePageUpdate={handlePageUpdate}
            authContext={authContext}
          />
        )}

        {/* Filters and Add Button */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="w-full sm:w-1/4">
              <label className="block mb-1 text-xs font-medium text-gray-700">Search Products</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter product name..."
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="w-full sm:w-1/4">
              <label className="block mb-1 text-xs font-medium text-gray-700">Filter by Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-md transition-colors text-sm mt-5 sm:mt-0"
              onClick={addSaleModalSetting}
            >
              Add Sale
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Store Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Stock Sold
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Sale Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Price Per Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Total Sale Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSales.map((element) => (
                  <tr key={element.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-900">{element.product_name || "N/A"}</td>
                    <td className="px-6 py-4 text-gray-700">{element.store_name || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">{element.stock_sold || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {formatDate(element.sale_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      Rs.{element.price_per_unit ? Number(element.price_per_unit).toFixed(2) : "0.00"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      Rs.{element.total_sale_amount ? Number(element.total_sale_amount).toFixed(2) : "0.00"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredSales.length === 0 && (searchQuery || filterDate) && (
            <div className="text-center text-gray-500 py-6">
              No sales found matching the search or date filter.
            </div>
          )}
        </div>

        {filteredSales.length === 0 && !searchQuery && !filterDate && !error && (
          <div className="text-center text-gray-500 mt-6">No sales data available.</div>
        )}
      </div>
    </div>
  );
}

export default Sales;